import React from "react";
import { ChevronRight } from "lucide-react";

/**
 * Shared atoms for diagnostic fault-trees (WarningLight, Noise/Vibration,
 * Intermittent, Brakes, Performance). Keeps every tree visually + structurally
 * identical so the FOH learns one capture pattern that applies everywhere.
 */

export function Branch({ step, label, subtitle, testId, children }) {
  return (
    <section className="space-y-1.5" data-testid={testId}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[11px] font-bold">
          {step}
        </span>
        <div className="min-w-0">
          <p className="text-[14.5px] font-semibold text-slate-900 leading-snug">
            {label}
          </p>
          {subtitle && (
            <p className="text-[12.5px] text-slate-500 leading-snug">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div className="pl-7">{children}</div>
    </section>
  );
}

export function Chips({
  value,
  options,
  onChange,
  testId,
  multi = false,
  tone = "rose",
}) {
  const selected = multi ? new Set(value || []) : value;
  const accent = TONE[tone] || TONE.rose;

  return (
    <div className="flex gap-1.5 flex-wrap">
      {options.map((opt) => {
        const isOn = multi ? selected.has(opt.v) : selected === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            onClick={() => {
              if (multi) {
                const next = new Set(selected);
                if (next.has(opt.v)) next.delete(opt.v);
                else next.add(opt.v);
                onChange(Array.from(next));
              } else {
                onChange(opt.v);
              }
            }}
            className={`px-3 py-1.5 rounded-full border text-[13.5px] font-medium transition-colors ${
              isOn
                ? `${accent.borderOn} ${accent.bgOn} ${accent.textOn} shadow-sm`
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
            data-testid={`${testId}-${opt.v}`}
          >
            {opt.label}
            {!multi && isOn && (
              <ChevronRight className="inline w-3 h-3 ml-0.5 opacity-60" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TreeHeader({ eyebrow, title, tone = "rose", right }) {
  const grad = HEADER_GRAD[tone] || HEADER_GRAD.rose;
  return (
    <header
      className={`px-4 py-3 ${grad} border-b border-slate-200 flex items-center justify-between gap-3`}
    >
      <div>
        <p className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${TONE[tone]?.eyebrow || "text-rose-700"}`}>
          {eyebrow}
        </p>
        <h4 className="text-[16px] font-bold text-slate-900 leading-tight">
          {title}
        </h4>
      </div>
      {right}
    </header>
  );
}

export function FieldRow({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12.5px] font-semibold text-slate-700 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

const TONE = {
  rose: {
    borderOn: "border-rose-500",
    bgOn: "bg-rose-50",
    textOn: "text-rose-800",
    eyebrow: "text-rose-700",
  },
  amber: {
    borderOn: "border-amber-500",
    bgOn: "bg-amber-50",
    textOn: "text-amber-800",
    eyebrow: "text-amber-700",
  },
  blue: {
    borderOn: "border-blue-500",
    bgOn: "bg-blue-50",
    textOn: "text-blue-800",
    eyebrow: "text-blue-700",
  },
  teal: {
    borderOn: "border-teal-500",
    bgOn: "bg-teal-50",
    textOn: "text-teal-800",
    eyebrow: "text-teal-700",
  },
};

const HEADER_GRAD = {
  rose: "bg-gradient-to-r from-rose-50 via-amber-50 to-white",
  amber: "bg-gradient-to-r from-amber-50 via-orange-50 to-white",
  blue: "bg-gradient-to-r from-blue-50 via-sky-50 to-white",
  teal: "bg-gradient-to-r from-teal-50 via-emerald-50 to-white",
};
